import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SocialLink } from "../../components/brand/SocialLink";
import { PromotionRatingLabel } from "../../components/brand/PromotionRatingLabel";
import {
  PROFILE_FIELDS,
  PROMOTION_COPY,
} from "../../components/brand/constants";
import { brandsService } from "../../services/brands";

const Overview = ({ brand, setBrand, hash }) => {
  const [promotionRating, setPromotionRating] = useState(
    brand?.promotion_rating ?? 5,
  );
  const [savingPromotion, setSavingPromotion] = useState(false);
  const [promotionDropdownOpen, setPromotionDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    setPromotionRating(brand?.promotion_rating ?? 5);
  }, [brand?.promotion_rating]);

  useEffect(() => {
    if (!promotionDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPromotionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [promotionDropdownOpen]);

  const handlePromotionSelect = (num) => {
    setPromotionRating(num);
    setPromotionDropdownOpen(false);
    setSavingPromotion(true);
    brandsService.updateBrand(
      hash,
      { promotion_rating: num },
      (response) => {
        setBrand(response.data);
        setSavingPromotion(false);
      },
      () => setSavingPromotion(false),
    );
  };

  if (!brand) return null;

  const rating = promotionRating ?? 5;
  const pct = (rating / 10) * 100;
  const topAudienceFits = Array.isArray(brand.top_audience_fits)
    ? brand.top_audience_fits
    : [];

  return (
    <div className="space-y-6">
      <div className="bg-theme-secondary rounded-lg p-6 border border-theme-secondary flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-theme-text mb-2">
            {brand.name || "(Unnamed brand)"}
          </h2>
          {brand.domain && (
            <p className="text-sm mb-2">
              <a
                href={brand.domain}
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-primary hover:underline"
              >
                {brand.domain}
              </a>
            </p>
          )}
          {(brand.creators || []).length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {brand.creators.map((c) => (
                <SocialLink
                  key={`${c.platform}-${c.platform_id}`}
                  platform={c.platform}
                  platformId={c.platform_id}
                  className="inline-flex"
                />
              ))}
            </div>
          )}
          <div className="mt-4">
            <h3 className="text-xs font-medium text-theme-text opacity-70 uppercase tracking-wide mb-2">
              Top audiences
            </h3>
            {topAudienceFits.length === 0 ? (
              <p className="text-sm text-theme-text opacity-70">
                Audience fit scores are still calculating.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {topAudienceFits.map((fit) => (
                  <li
                    key={fit.audience_hash}
                    className="flex items-center justify-between gap-4"
                  >
                    <Link
                      to={`/audience/${fit.audience_hash}/`}
                      className="text-sm text-theme-primary hover:underline truncate max-w-[260px]"
                      title={
                        fit.audience_name || fit.category_name || "Audience"
                      }
                    >
                      {fit.audience_name || fit.category_name || "Audience"}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div
          className="relative shrink-0 flex flex-col items-center"
          ref={dropdownRef}
        >
          <p className="text-sm font-medium text-theme-text mb-2">
            <PromotionRatingLabel />
          </p>
          <button
            type="button"
            onClick={() => setPromotionDropdownOpen((o) => !o)}
            disabled={savingPromotion}
            className="relative flex flex-col items-center cursor-pointer disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-theme-primary rounded"
            aria-expanded={promotionDropdownOpen}
            aria-haspopup="listbox"
            aria-label="Promotion rating"
          >
            <div className="relative w-32 h-16 overflow-visible flex-shrink-0">
              <svg
                viewBox="-12 -12 124 64"
                className="w-full h-full block overflow-visible"
                aria-hidden
                style={{ overflow: "visible" }}
              >
                <defs>
                  <linearGradient
                    id="promoGradient"
                    x1="0"
                    y1="50"
                    x2="100"
                    y2="50"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%" stopColor="#22c55e" />
                    <stop offset="100%" stopColor="#ef4444" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0 50 A 50 50 0 0 1 100 50"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="12"
                  className="text-theme-secondary opacity-25"
                />
                <path
                  d="M 0 50 A 50 50 0 0 1 100 50"
                  fill="none"
                  stroke="url(#promoGradient)"
                  strokeWidth="12"
                  strokeDasharray={`${(pct / 100) * 157} 157`}
                  className="transition-all duration-300"
                />
              </svg>
              <span className="absolute left-0 right-0 bottom-0.5 text-center text-2xl font-bold text-theme-text tabular-nums pointer-events-none leading-none">
                {rating}
              </span>
            </div>
          </button>
          <p className="text-xs text-theme-text opacity-80 mt-1 text-center max-w-[180px]">
            {PROMOTION_COPY[rating] ?? PROMOTION_COPY[5]}
          </p>
          {promotionDropdownOpen && (
            <div
              className="absolute mt-2 z-10 w-72 max-h-64 overflow-y-auto rounded-lg border border-theme-secondary bg-theme-main shadow-lg py-1"
              role="listbox"
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="option"
                  aria-selected={n === rating}
                  onClick={() => handlePromotionSelect(n)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-theme-secondary transition-colors ${
                    n === rating
                      ? "bg-theme-secondary text-theme-primary font-medium"
                      : "text-theme-text"
                  }`}
                >
                  {n}/10 - {PROMOTION_COPY[n]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-theme-secondary rounded-lg p-6 border border-theme-secondary">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-theme-text opacity-80">
            Profile
          </h3>
          <Link
            to="/brand/profile/"
            className="text-sm text-theme-primary hover:underline"
          >
            Edit
          </Link>
        </div>
        <div className="space-y-4">
          {PROFILE_FIELDS.map(({ key, label }) => {
            const value = brand[key];
            return (
              <div key={key}>
                <h4 className="text-xs font-medium text-theme-text opacity-60 uppercase tracking-wide mb-1">
                  {label}
                </h4>
                <p className="text-theme-text whitespace-pre-wrap text-sm">
                  {value != null && value !== "" ? value : "—"}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Overview;
